import {setTitle as setDocumentTitle} from '@github-ui/document-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isMobile} from '@github-ui/get-os'
import type {IssueViewerPageQuery} from '@github-ui/server-persisted-queries/types/IssueViewerPage'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {XIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'

import {ContentWrapper} from '../ContentWrapper'
import type {IssueNode} from '../issue-viewer-page-query-types'
import type {OptionConfig} from '../OptionConfig'
import styles from './Header.module.css'
import {HeaderEditor} from './HeaderEditor'
import {HeaderMetadata} from './HeaderMetadata'
import type {HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import {HeaderViewer} from './HeaderViewer'
import {useIssueHeaderDataContext} from './IssueHeaderDataContext'
import {JumpToBottom} from './JumpToBottom'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import type {TrackedByFragment} from './TrackedBy'
import {useTitleMirror} from './use-title-mirror'

type HeaderRepository = NonNullable<IssueNode['repository']>
type HeaderRepositoryOwner = NonNullable<HeaderRepository['owner']>
type IssueViewerPageRepository = NonNullable<IssueViewerPageQuery['repository']>
type IssueViewerPageIssue = NonNullable<IssueViewerPageRepository['issue']>
type IssueViewerPageOwner = IssueViewerPageIssue['repository']['owner']
type HeaderIssueType = NonNullable<IssueNode['issueType']>
type HeaderClosedByPullRequestNode = NonNullable<
  NonNullable<NonNullable<IssueNode['closedByPullRequestsReferences']>['nodes']>[number]
>

export type HeaderFragment = Pick<
  IssueNode,
  | 'title'
  | 'titleHTML'
  | 'number'
  | 'url'
  | 'viewerCanUpdateNext'
  | 'id'
  | 'subIssuesSummary'
  | 'state'
  | 'stateReason'
  | 'linkedPullRequests'
  | 'labels'
  | 'milestone'
  | 'assignedActors'
> & {
  readonly status?: IssueViewerPageIssue['status']
  readonly viewerCanSetStatus?: IssueViewerPageIssue['viewerCanSetStatus']
  readonly repository: Pick<
    HeaderRepository,
    'name' | 'nameWithOwner' | 'isArchived' | 'isPrivate' | 'isWritable' | 'id'
  > & {
    readonly owner: Pick<HeaderRepositoryOwner, 'login' | '__typename'> & {
      readonly planningStatuses?: IssueViewerPageOwner['planningStatuses']
    }
  }
  readonly issueType: Pick<HeaderIssueType, 'name' | 'color'> | null
  readonly agentAssignments: ReadonlyArray<{readonly __typename: string}>
  readonly closedByPullRequestsReferences: {
    readonly nodes: ReadonlyArray<Pick<
      HeaderClosedByPullRequestNode,
      'id' | 'number' | 'url' | 'state' | 'databaseId'
    > | null> | null
  } | null
}

type HeaderBaseProps = {
  optionConfig: OptionConfig
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
}

type HeaderProps = HeaderBaseProps & {
  issue: HeaderFragment
  hideJumpToBottom?: boolean
  showActivityTab?: boolean
}

/**
 * The refetch has a single owner per page, `IssueViewer`, so that this header and the sticky header
 * always show the same answer for one request between them.
 */
export function Header({
  issue,
  trackedBySecondaryData,
  subIssueSecondaryData,
  linkedSecurityAlertsData,
  optionConfig,
  hideJumpToBottom,
  showActivityTab,
}: HeaderProps) {
  const refetchedHeader = useIssueHeaderDataContext()
  const data = useMemo(
    () => ({
      ...issue,
      ...refetchedHeader,
      issueType: issue.issueType,
      repository: {...issue.repository, ...refetchedHeader.repository},
    }),
    [issue, refetchedHeader],
  )

  const isTransferInProgress = refetchedHeader.isTransferInProgress

  const {
    id,
    title: issueTitle,
    number,
    repository: {nameWithOwner},
  } = data

  const titleRef = useRef<HTMLInputElement>(null)
  const [isIssueTitleEditActive, setIsIssueTitleEditActive] = useState(false)

  // Local title that mirrors the server-provided `issueTitle` when not editing.
  // Updates synchronously in the same render so downstream consumers see the
  // synced value without an extra commit.
  const [title, setTitle] = useTitleMirror(issueTitle, isIssueTitleEditActive)

  const shouldUpdateDocumentTitle =
    optionConfig.selectedProjectId === undefined && !optionConfig.shouldSkipSetDocumentTitle
  useEffect(() => {
    if (issueTitle && shouldUpdateDocumentTitle) {
      setDocumentTitle(`${title} · Issue #${number} · ${nameWithOwner}`)
    }
  }, [shouldUpdateDocumentTitle, issueTitle, nameWithOwner, number, title])

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = title
    const newValue = e.target.value
    if (newValue !== null && newValue !== oldValue) {
      setTitle(newValue)
    }
  }

  // Focus on title when user clicks on edit
  useEffect(() => {
    if (isIssueTitleEditActive && titleRef.current) {
      titleRef.current.focus()
    }
  }, [isIssueTitleEditActive])

  const {isSticky, hasRoots, sensorRef} = useStickyHeader()
  const [transferWarningDismissed, setTransferWarningDismissed] = useState(false)
  const showTransferWarning = isTransferInProgress && !transferWarningDismissed

  return (
    <>
      <ContentWrapper>
        {showTransferWarning && (
          <Flash aria-live="polite" className={styles.transferWarningFlash}>
            Issue transfer in progress
            <IconButton
              variant="invisible"
              aria-label="Dismiss issue is in transfer notification"
              icon={XIcon}
              onClick={() => setTransferWarningDismissed(true)}
            />
          </Flash>
        )}
        {isIssueTitleEditActive ? (
          <HeaderEditor
            issueId={id}
            issueNumber={number}
            nameWithOwner={nameWithOwner}
            title={title}
            titleRef={titleRef}
            onTitleChange={onTitleChange}
            onIssueUpdate={optionConfig.onIssueUpdate}
            cancelIssueTitleEdit={() => {
              setIsIssueTitleEditActive(false)
            }}
            isDirty={issueTitle !== title}
          />
        ) : (
          <HeaderViewer
            headerViewerData={data}
            optionConfig={optionConfig}
            showActivityTab={showActivityTab}
            setIsIssueTitleEditActive={setIsIssueTitleEditActive}
            issueTitle={title}
          />
        )}
      </ContentWrapper>
      <HeaderMetadata
        headerMetadataData={data}
        optionConfig={optionConfig}
        showActivityTab={showActivityTab}
        isSticky={hasRoots && isSticky}
        setIsIssueTitleEditActive={setIsIssueTitleEditActive}
        linkedSecurityAlertsData={linkedSecurityAlertsData}
        trackedBySecondaryData={trackedBySecondaryData}
        subIssueSecondaryData={subIssueSecondaryData}
        parent={refetchedHeader.parent}
      />
      {isMobile() && !hideJumpToBottom && <JumpToBottom />}
      <div ref={sensorRef} className={styles.stickyObservableBox} />
      <div
        className={clsx(
          styles.HeaderContentWrapper,
          isFeatureEnabled('issues_sticky_header') && styles.HeaderContentWrapper_stickyHeader,
          isFeatureEnabled('issue_inline_avatars') && styles.HeaderContentWrapper_inlineAvatars,
        )}
      >
        <ContentWrapper>
          <div className={styles.headerSpacer} />
        </ContentWrapper>
      </div>
    </>
  )
}
